// Rasterizes an affine transform (scale + rotation + translation) into the
// target raster layer's pixel buffer. The supplied matrix maps the layer's
// LOCAL source coords (0..w, 0..h) to a NEW LOCAL coord system of the output
// buffer of size (newWidth, newHeight). Wherever the transform places the
// layer in document space is captured by the caller in `newX/newY`.
//
// Behaviour matches the rest of the pixel-mutating commands (PaintStroke,
// ApplyFilter): the layer's PixelBuffer is replaced (not mutated in place,
// because the dimensions change), and the Document tree gets a fresh layer
// node referencing the new buffer. `invert` snapshots the pre-transform
// pixel data so a redo→undo cycle restores it byte-for-byte.

import type { Document } from "../document/Document";
import type { Layer, RasterLayer } from "../document/Layer";
import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Affine2D } from "../pixel/affineTransform";
import { transformBuffer } from "../pixel/affineTransform";
import type { Command } from "./Command";

interface PreSnapshot {
  readonly data: Uint8ClampedArray;
  readonly width: number;
  readonly height: number;
  readonly x: number;
  readonly y: number;
}

/**
 * Frozen pre-transform-session layer state (audit i20). Free Transform's
 * interactive drag builds a brand-new `TransformLayerCommand` on every
 * pointer move and emits it as a transient — but `EditorState.applyTransient`
 * has no snapshot/rollback of its own, so it mutates the live doc in place.
 * Without this, each transient's `apply()` would warp whatever the doc
 * CURRENTLY holds (already-warped pixels from the previous move) instead of
 * the original, compounding the transform on every move.
 *
 * When `original` is supplied, the command instead always reads its source
 * pixels from this frozen snapshot and always restores to it on invert —
 * so repeated transients are idempotent (non-cumulative: they each rebuild
 * fresh from `original`), and a commit's undo lands on the true pre-session
 * state regardless of how many transients ran in between.
 */
export interface OriginalLayerSnapshot {
  readonly pixels: PixelBuffer;
  readonly x: number;
  readonly y: number;
}

export class TransformLayerCommand implements Command {
  readonly kind = "transform-layer";
  private snapshot: PreSnapshot | null = null;

  constructor(
    private readonly layerId: string,
    // Affine that maps source-buffer coords → output-buffer coords (local).
    private readonly matrix: Affine2D,
    private readonly newWidth: number,
    private readonly newHeight: number,
    // Document-space top-left of the output buffer.
    private readonly newX: number,
    private readonly newY: number,
    private readonly sampling: "nearest" | "bilinear" = "bilinear",
    // See OriginalLayerSnapshot doc comment above. Optional so existing
    // one-shot callers (and this file's own tests) keep working unchanged —
    // when omitted, behaviour is exactly as before (source + invert target
    // come from the doc's current layer at first apply()).
    private readonly original?: OriginalLayerSnapshot,
  ) {}

  apply(doc: Document): Document {
    const layer = findRaster(doc.layers, this.layerId);
    if (!layer) return doc;

    if (this.snapshot === null) {
      const base = this.original ?? { pixels: layer.pixels, x: layer.x, y: layer.y };
      this.snapshot = {
        data: new Uint8ClampedArray(base.pixels.data),
        width: base.pixels.width,
        height: base.pixels.height,
        x: base.x,
        y: base.y,
      };
    }

    const source = this.original ? this.original.pixels : layer.pixels;
    const next = transformBuffer(source, this.matrix, {
      outputWidth: this.newWidth,
      outputHeight: this.newHeight,
      sampling: this.sampling,
    });

    return {
      ...doc,
      layers: replaceRaster(doc.layers, this.layerId, {
        ...layer,
        x: this.newX,
        y: this.newY,
        pixels: next,
      }),
    };
  }

  invert(_before: Document, _after: Document): Command {
    const snap = this.snapshot;
    if (snap === null) return new NoOpCommand();
    return new RestoreTransformCommand(this.layerId, snap);
  }
}

// Inverse of a TransformLayerCommand. Holds the pre-transform pixel data
// and layer offset. Redo'ing this command (i.e. inverting it) snapshots
// the current pixels so the undo→redo→undo cycle remains lossless.
class RestoreTransformCommand implements Command {
  readonly kind = "transform-layer/restore";
  constructor(
    private readonly layerId: string,
    private readonly snapshot: PreSnapshot,
  ) {}

  apply(doc: Document): Document {
    const layer = findRaster(doc.layers, this.layerId);
    if (!layer) return doc;
    const buf = new PixelBuffer(this.snapshot.width, this.snapshot.height, layer.pixels.mode);
    buf.data.set(this.snapshot.data);
    buf.markDirty();
    return {
      ...doc,
      layers: replaceRaster(doc.layers, this.layerId, {
        ...layer,
        x: this.snapshot.x,
        y: this.snapshot.y,
        pixels: buf,
      }),
    };
  }

  invert(before: Document, _after: Document): Command {
    const layer = findRaster(before.layers, this.layerId);
    if (!layer) return new NoOpCommand();
    const snap: PreSnapshot = {
      data: new Uint8ClampedArray(layer.pixels.data),
      width: layer.pixels.width,
      height: layer.pixels.height,
      x: layer.x,
      y: layer.y,
    };
    return new RestoreTransformCommand(this.layerId, snap);
  }
}

class NoOpCommand implements Command {
  readonly kind = "transform-layer/noop";
  apply(doc: Document): Document {
    return doc;
  }
  invert(): Command {
    return new NoOpCommand();
  }
}

// ---------------------------------------------------------------------------
// Tree helpers
// ---------------------------------------------------------------------------

function findRaster(layers: readonly Layer[], id: string): RasterLayer | null {
  for (const l of layers) {
    if (l.id === id && l.kind === "raster") return l;
    if (l.kind === "group") {
      const inner = findRaster(l.children, id);
      if (inner) return inner;
    }
  }
  return null;
}

function replaceRaster(
  layers: readonly Layer[],
  id: string,
  next: RasterLayer,
): Layer[] {
  return layers.map((l) => {
    if (l.id === id && l.kind === "raster") return next;
    if (l.kind === "group") {
      return { ...l, children: replaceRaster(l.children, id, next) };
    }
    return l;
  });
}
